import { Box, Button } from '@mui/material';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { filter, take } from 'rxjs';

import { Wa_CreateCustomerInput } from '../../../../../graphql/generatedModel.ts';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler.ts';
import { modeService } from '../../../../../shared/services/mode/mode.service.ts';
import { GeneralForm } from './components/generalForm.component.tsx';
import { customersListState } from '../../../states/customersList.state.ts';
import { IGeneralTabProps } from './general.tab.tsx';
import { CreateCustomerDataRes, customerService } from '../../../services/customer.service.ts';
import { customerTabLoadingService } from '../customerTabLoading.service.ts';
import { storageHelper } from '../../../../../shared/helpers/storage/index.ts';

export const GeneralCreateTab: FC<
  Pick<IGeneralTabProps, 'mode' | 'loading' | 'customerNoDisabled'>
> = ({ mode, loading, customerNoDisabled = false }) => {
  const { t } = useTranslation();
  const { getValues, handleSubmit, reset } = useFormContext();

  const onSubmitForm = () => {
    customerTabLoadingService.pub.loading(true);
    const { customerProfileId, ...data } = getValues() as ICreateCustomerValues;
    customerService.pub.createCustomer({
      data: {
        ...data,
        customerNo: data.customerNo as string,
      },
      customerProfileId,
    });
  };

  const onCancelCreateMode = () => {
    customersListState.pub.selectRecord(storageHelper.session.getItem('customer.selectedRecord'));
    // keep this reset() for server validation handler
    // this keepDirty blocks pub.save() in handleSubmitValue from being called again after copying or creating a new entry
    reset(undefined, { keepDirty: false });
    modeService.pub.mode('edit');
  };

  useEffect(() => {
    customersListState.pub.unselectRecord();

    const createCustomerSub = customerService.sub
      .createCustomer()
      .pipe(
        take(1),
        responseHandler<CreateCustomerDataRes | undefined>({
          success: () => 'customer.customer_saved',
          customErrorHandler: () => {
            customerTabLoadingService.pub.loading(false);
            return 'common.error_chnages_not_saved';
          },
        }),
        filter((v) => v !== undefined),
        take(1),
      )
      .subscribe(() => customerTabLoadingService.pub.loading(false));

    return () => {
      createCustomerSub.unsubscribe();
    };
  }, []);

  return (
    <>
      <GeneralForm mode={mode} loading={loading} customerNoDisabled={customerNoDisabled} />
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          gap: 2,
        }}
      >
        <Button variant='outlined' onClick={onCancelCreateMode} disabled={customerNoDisabled}>
          {t('common.cancel')}
        </Button>
        <Button
          variant='contained'
          type='submit'
          onClick={handleSubmit(onSubmitForm)}
          disabled={customerNoDisabled}
        >
          {t('customer.create_customer')}
        </Button>
      </Box>
    </>
  );
};

export interface ICreateCustomerValues extends Omit<Wa_CreateCustomerInput, 'customerNo'> {
  customerNo: string | null;
  customerProfileId?: string | null;
}
